import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging, BellSlash, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";

const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushNotificationsCard = ({ compact = false }) => {
  const { user } = useAuth();
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  useEffect(() => {
    if (!supported) { setState("unsupported"); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setState(sub ? "enabled" : "disabled"))
      .catch(() => setState("disabled"));
  }, [supported]);

  if (user?.role !== "master") return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato dal browser: abilitalo dalle impostazioni del sito");
        return;
      }
      const { data } = await api.get("/push/public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      const { data: res } = await api.post("/push/subscribe", sub.toJSON());
      setState("enabled");
      toast.success(res.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Attivazione non riuscita su questo browser");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setState("disabled");
      toast.success("Notifiche push disattivate su questo dispositivo");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notifica di prova inviata: controlla il dispositivo");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const enabled = state === "enabled";
  if (compact) {
    return (
      <div data-testid="push-notifications-card">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
          {enabled
            ? <BellRinging size={18} weight="duotone" className="text-emerald-400 shrink-0" />
            : <BellSlash size={18} weight="duotone" className="text-white/40 shrink-0" />} Notifiche push
        </h3>
        <p className="text-xs text-slate-400 mb-3">Ricevi gli avvisi SEO e i backup falliti come notifica su questo dispositivo, oltre che via email.</p>
        {state === "unsupported" && (
          <p className="text-[11px] text-amber-300/80 mb-3" data-testid="push-unsupported">
            Questo browser non supporta le notifiche push. Su iPhone/iPad funzionano solo dall'app installata.
          </p>
        )}
        <div className="space-y-2">
          {enabled && (
            <Button data-testid="push-test-button" onClick={sendTest} variant="ghost"
              className="w-full h-10 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Invia prova
            </Button>
          )}
          <Button data-testid="push-toggle-button" onClick={enabled ? disable : enable}
            disabled={busy || state === "loading" || state === "unsupported"}
            className={`w-full h-10 rounded-full text-xs font-semibold ${enabled ? "bg-transparent border border-red-400/50 text-red-400 hover:bg-red-400/10" : "bg-white text-black hover:bg-gray-200"}`}>
            {busy ? "Attendere..." : enabled ? "Disattiva su questo dispositivo" : "Attiva le notifiche"}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-7 rounded-lg bg-surface border border-surface-border mt-8 animate-fade-up" data-testid="push-notifications-card">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-start gap-3 min-w-0">
          {enabled
            ? <BellRinging size={30} weight="duotone" className="text-emerald-400 shrink-0" />
            : <BellSlash size={30} weight="duotone" className="text-white/40 shrink-0" />}
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-semibold tracking-tight">Notifiche push</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Ricevi gli avvisi SEO (siti in errore, pagine rimosse dall'indice) e i backup falliti come notifica su questo dispositivo, oltre che via email.
            </p>
            {state === "unsupported" && (
              <p className="text-[11px] text-amber-300/80 mt-2" data-testid="push-unsupported">
                Questo browser non supporta le notifiche push. Su iPhone/iPad funzionano solo dall'app installata (Aggiungi alla schermata Home).
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {enabled && (
            <Button data-testid="push-test-button" onClick={sendTest} variant="ghost"
              className="h-10 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Invia prova
            </Button>
          )}
          <Button data-testid="push-toggle-button" onClick={enabled ? disable : enable}
            disabled={busy || state === "loading" || state === "unsupported"}
            className={`h-10 px-5 rounded-full text-xs font-semibold ${enabled ? "bg-transparent border border-red-400/50 text-red-400 hover:bg-red-400/10" : "bg-white text-black hover:bg-gray-200"}`}>
            {busy ? "Attendere..." : enabled ? "Disattiva su questo dispositivo" : "Attiva le notifiche"}
          </Button>
        </div>
      </div>
    </div>
  );
};
